import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Field, useFormState, useForm } from 'react-final-form';
import { InputNumber, Table } from 'antd';
import { fuelsUsedFormFields } from '../../constants/formsAddEquipment';
import { translateFuelTypeClassificator } from '../../utils/translateHelpers';

const GhgInput = ({ name, disabled }) => (
  <Field name={name}>
    {({ input, meta }) => (
      <InputNumber
        value={input.value}
        onChange={(val) => input.onChange(val ?? null)}
        disabled={disabled}
        step={0.01}
        precision={2}
        style={{ width: '100%' }}
        status={
          meta.touched && (input.value == null || input.value === '')
            ? 'error'
            : undefined
        }
      />
    )}
  </Field>
);

GhgInput.propTypes = {
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

const hasGhgForMp = (ghgs, mpId) =>
  ghgs.some((g) => g.measurementPointId === mpId);

const MeasurementPointGhgTable = ({
  measurementPoints,
  t,
  isOpen,
  monthlyFuelDataConfirmed,
  monthlyGoIssued,
}) => {
  const { values } = useFormState({
    subscription: { values: true },
  });
  const form = useForm();

  const fuels = values?.[fuelsUsedFormFields.TYPES] || [];
  const selectedFuels = useMemo(
    () =>
      fuels
        .map((fuel, index) => ({ fuel, index }))
        .filter(({ fuel }) => fuel?.[fuelsUsedFormFields.IS_FUEL_USED]),
    [fuels]
  );

  // every selected feedstock needs a GHG row per measurement point before it can be saved
  useEffect(() => {
    selectedFuels.forEach(({ fuel, index: fuelIdx }) => {
      const ghgs = fuel[fuelsUsedFormFields.MEASUREMENT_POINT_FUEL_GHGS] || [];
      const missing = measurementPoints.filter(
        (mp) => !hasGhgForMp(ghgs, mp.measurementPointId)
      );
      if (missing.length === 0) {
        return;
      }
      form.change(
        `${fuelsUsedFormFields.TYPES}[${fuelIdx}].${fuelsUsedFormFields.MEASUREMENT_POINT_FUEL_GHGS}`,
        [
          ...ghgs,
          ...missing.map((mp) => ({
            measurementPointId: mp.measurementPointId,
            ghgIntensity: null,
          })),
        ]
      );
    });
  }, [selectedFuels, measurementPoints, form]);

  if (selectedFuels.length === 0 || measurementPoints.length === 0) {
    return null;
  }

  const disabled = !isOpen || (monthlyFuelDataConfirmed && monthlyGoIssued);

  const columns = [
    {
      title: t('ghgTable.fuelType'),
      key: 'fuelType',
      width: 200,
      render: (_, record) => translateFuelTypeClassificator(record.fuelType),
    },
    ...measurementPoints.map((mp) => ({
      title: mp.measurementPointEicz,
      key: `mp_${mp.measurementPointId}`,
      width: 200,
      render: (_, record) => {
        const ghgs =
          record.fuel[fuelsUsedFormFields.MEASUREMENT_POINT_FUEL_GHGS] || [];
        const ghgIdx = ghgs.findIndex(
          (g) => g.measurementPointId === mp.measurementPointId
        );
        if (ghgIdx < 0) return null;
        const fieldName = `${fuelsUsedFormFields.TYPES}[${record.fuelIdx}].${fuelsUsedFormFields.MEASUREMENT_POINT_FUEL_GHGS}[${ghgIdx}].ghgIntensity`;
        return <GhgInput name={fieldName} disabled={disabled} />;
      },
    })),
  ];

  const dataSource = selectedFuels.map(({ fuel, index }) => ({
    key: `fuel_${index}`,
    fuelIdx: index,
    fuelType: fuel[fuelsUsedFormFields.FUEL_TYPE],
    fuel,
  }));

  return (
    <div style={{ marginTop: 16, marginBottom: 16 }}>
      <b>{t('ghgTable.title')}</b>
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        size="small"
        bordered
        style={{ marginTop: 8 }}
      />
    </div>
  );
};

MeasurementPointGhgTable.propTypes = {
  measurementPoints: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  monthlyFuelDataConfirmed: PropTypes.bool.isRequired,
  monthlyGoIssued: PropTypes.bool.isRequired,
};

export default MeasurementPointGhgTable;
